/**
 * Auth store — Zustand. Mirrors what the gateway returns on /me, plus
 * a derived `status` for route guards.
 *
 * The store never persists tokens — they live in httpOnly cookies set
 * by chaos-auth-service. We persist only the last-known user + active
 * workspace pointer to avoid a "splash" on every reload.
 */
import type { MeResponse, Workspace, WorkspaceMembership } from '@chaos/types/auth';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthSlice {
  status: AuthStatus;
  user: MeResponse['user'] | null;
  workspaces: Array<{ workspace: Workspace; membership: WorkspaceMembership }>;
  activeWorkspaceId: string | null;

  setLoading: () => void;
  setSession: (me: MeResponse) => void;
  setUnauthenticated: () => void;
  setActiveWorkspace: (workspaceId: string) => void;
}

export const useAuthStore = create<AuthSlice>()(
  persist(
    (set) => ({
      status: 'idle',
      user: null,
      workspaces: [],
      activeWorkspaceId: null,

      setLoading: () => set({ status: 'loading' }),
      setSession: (me) => {
        const firstWorkspaceId = me.workspaces[0]?.workspace.id ?? null;
        set((state) => ({
          status: 'authenticated',
          user: me.user,
          workspaces: me.workspaces,
          activeWorkspaceId: state.activeWorkspaceId ?? firstWorkspaceId,
        }));
      },
      setUnauthenticated: () =>
        set({
          status: 'unauthenticated',
          user: null,
          workspaces: [],
          activeWorkspaceId: null,
        }),
      setActiveWorkspace: (workspaceId) => set({ activeWorkspaceId: workspaceId }),
    }),
    {
      name: 'chaos-auth',
      // Persist only the small public bits — the cookies are the source
      // of truth for the actual access decision.
      partialize: (state) => ({
        user: state.user,
        workspaces: state.workspaces,
        activeWorkspaceId: state.activeWorkspaceId,
      }),
    },
  ),
);
